import { Menu } from "@headlessui/react";
import clsx from "clsx";

import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";

import { getNavItemStateClasses } from "./navItemStateClasses";

type NavItemLinkProps = {
  link: string;
  label: string;
  /** Use the inverted (light-on-dark) variant. Default: false. */
  inverted?: boolean;
  /** Disables the item. Default: false. */
  disabled?: boolean;
};

/**
 * Link nav item for use inside a Headless UI Menu.
 * Implements all states (default, hovered, pressed, focused, disabled)
 * and both variants (default, inverted).
 *
 * Must be wrapped in a {@link Menu.Items} component.
 *
 * @example
 * ```jsx
 * <Menu>
 *   <Menu.Items>
 *     <NavItemLink
 *       link="https://example.com"
 *       label="Example"
 *       inverted
 *     />
 *   </Menu.Items>
 * </Menu>
 * ```
 *
 * @param link - The URL opened when the link is clicked.
 * @param label - Unique label text displayed in the menu.
 * @param inverted - Use the inverted (light-on-dark) variant. Default: false.
 * @param disabled - Disables the item. Default: false.
 */
export const NavItemLink = ({
  label,
  link,
  inverted = false,
  disabled = false,
}: NavItemLinkProps) => {
  return (
    <Menu.Item key={label} disabled={disabled}>
      {({ active }) => (
        <a
          href={disabled ? undefined : link}
          className={clsx(
            "group relative block w-fit rounded-md p-2 text-base focus:outline-none",
            getNavItemStateClasses({ inverted, disabled, active }),
            disabled && "pointer-events-none",
          )}
          data-testid={label}
          target="_blank"
          rel="noreferrer"
        >
          {label}
          <FocusRing inverted={inverted} />
        </a>
      )}
    </Menu.Item>
  );
};
